// src/features/editor/blocksApi.ts
import { api } from '@/store/api'
import type { RootState } from '@/store'
import type { Block, BlockCreate, BlockCreateInput } from '@/types/blocks'
import { lessonsApi } from './lessonsApi'
import { normalizeArticleBlock } from './lib/articleBlock'
import { normalizeButtons } from './lib/blockButtons'
import { trimQuizAnswers } from './lib/quizAnswersTrim'
import { applyLookUpdates } from './lib/blockLook'
import { runOptimistic } from './lib/optimisticUpdate'
import { applyOrderUpdates, compareByOrder } from './lib/orderUtils'
import { blockPutRequests, runSequentialPuts } from './lib/sequentialPuts'
import { computeNextOrder } from './lib/computeNextOrder'

// Кнопки блока пересоздаются беком на каждом сохранении: `id` из ответа не переживает
// следующий PUT, а порядок на чтении берётся из `order`, а не из позиции в массиве
// (контракт кнопок §5). Приводим список к write-форме в одном месте — так это работает
// и для реордера блоков, и редакторам блоков помнить про кнопки не нужно.
// Туда же — обрезка хвостовых пробелов у вариантов квиза (`trimQuizAnswers`); то же тело
// уходит в оптимистичный патч кеша.
const withNormalizedBody = <T extends BlockCreate | BlockCreateInput>(body: T): T =>
  trimQuizAnswers({ ...body, buttons: normalizeButtons(body.buttons) })

export const blocksApi = api.injectEndpoints({
  endpoints: (build) => ({
    // The endpoint owns `order`: an explicit `body.order` wins (insert at a
    // position), otherwise the next order is computed from the store at POST
    // time. Component closures lag the cache by a render, so an order computed
    // in an event handler can be stale and hand out duplicates.
    createBlock: build.mutation<
      Block,
      // §23: courseId ОБЯЗАТЕЛЕН — без него бейдж плашки урока
      // (lesson.blocksCount, приезжает с деталью курса) не инвалидируется
      // и остаётся до-операционным. Опциональность тут раньше держала
      // инвариант на дисциплине вызывающего, а не на типах.
      {
        lessonId: number
        courseId: number
        body: BlockCreateInput
        /** Копия экрана: кеш ленты ведёт вызывающий (копия уже стоит на своём месте),
         *  поэтому ни вставки в конец, ни перечитывания списка — иначе копия на секунду
         *  уезжала в конец ленты, пока перестановка не возвращала её назад. */
        placedByCaller?: boolean
      }
    >({
      async queryFn({ lessonId, body }, { getState }, _extra, baseQuery) {
        const cached = lessonsApi.endpoints.getLessonBlocks.select(lessonId)(
          getState() as RootState,
        ).data
        const order = body.order ?? computeNextOrder(cached ?? [])
        const res = await baseQuery({
          url: `gamified-courses/lessons/${lessonId}/blocks`,
          method: 'POST',
          body: { ...withNormalizedBody(body), order },
        })
        if (res.error) return { error: res.error }
        // Граница api: та же нормализация, что и на чтении (getLessonBlocks) —
        // иначе созданный блок статьи уезжает в кеш ниже (onQueryStarted) без
        // верхнеуровневого articleId, минуя нормализацию до следующего рефетча.
        return { data: normalizeArticleBlock(res.data as Block) }
      },
      // Insert the created block into the cache immediately: the invalidation
      // refetch is async, and the next create must see this block to compute
      // its order.
      async onQueryStarted({ lessonId, placedByCaller }, { dispatch, queryFulfilled }) {
        if (placedByCaller) return
        try {
          const { data: created } = await queryFulfilled
          dispatch(
            lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
              draft.push(created)
              draft.sort(compareByOrder)
            }),
          )
        } catch {
          // mutation failed; nothing to insert
        }
      },
      // §23: деталь курса (GET /gamified-courses/{id}) несёт lesson.blocksCount,
      // которое рисует бейдж плашки урока — без инвалидации тега Course здесь
      // счётчик оставался до-операционным до ручного F5.
      invalidatesTags: (_r, _e, { lessonId, courseId, placedByCaller }) => [
        ...(placedByCaller ? [] : [{ type: 'Block' as const, id: `lesson-${lessonId}` }]),
        { type: 'Course', id: courseId },
      ],
    }),
    // NOTE: API requires FULL block body on PUT including the discriminator `type`.
    // Never pass a partial patch here — pass the full block as BlockCreate.
    updateBlock: build.mutation<Block, { id: number; lessonId: number; body: BlockCreate }>({
      query: ({ id, body }) => ({
        url: `gamified-courses/blocks/${id}`,
        method: 'PUT',
        body: withNormalizedBody(body),
      }),
      async onQueryStarted({ id, lessonId, body }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
            const idx = draft.findIndex((b) => b.id === id)
            if (idx < 0) return
            draft[idx] = { ...draft[idx], ...withNormalizedBody(body) } as Block
          }),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      invalidatesTags: (_r, _e, { lessonId }) => [{ type: 'Block', id: `lesson-${lessonId}` }],
    }),
    deleteBlock: build.mutation<void, { id: number; lessonId: number; courseId: number }>({
      // `404` = экрана уже нет (удалили раньше: быстрые удаления подряд, вторая вкладка) —
      // цель удаления достигнута, это успех, а не ошибка с двумя тостами.
      queryFn: async ({ id }, _api, _extra, baseQuery) => {
        const res = await baseQuery({ url: `gamified-courses/blocks/${id}`, method: 'DELETE' })
        if (res.error && res.error.status !== 404) return { error: res.error }
        return { data: undefined }
      },
      // Экран пропадает из ленты сразу, не дожидаясь ответа и перечитывания списка;
      // отказ сервера возвращает его на место (`runOptimistic`).
      async onQueryStarted({ id, lessonId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) =>
            draft.filter((b) => b.id !== id),
          ),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      // §23: см. комментарий у createBlock — та же плашка, тот же счётчик.
      // Список экранов НЕ перечитываем: экран уже убран из кеша (выше), и сервер
      // ответит тем же. Перечитывание после каждого удаления наслаивалось при быстрых
      // удалениях подряд — ответ на прошлое возвращал в ленту экран, удаляемый сейчас,
      // и каждое следующее удаление «тормозило». Курс перечитываем ради счётчика экранов.
      invalidatesTags: (_r, _e, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    reorderBlocks: build.mutation<
      void,
      { lessonId: number; updates: Array<{ id: number; body: BlockCreate }> }
    >({
      queryFn: ({ updates }, _api, _extra, baseQuery) =>
        runSequentialPuts(baseQuery, blockPutRequests(updates, withNormalizedBody)),
      async onQueryStarted({ lessonId, updates }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
            applyOrderUpdates(
              draft,
              updates.map((u) => ({ id: u.id, order: u.body.order })),
            )
          }),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      invalidatesTags: (_r, _e, { lessonId }) => [{ type: 'Block', id: `lesson-${lessonId}` }],
    }),
    // Рассылка оформления урока по его блокам. Bulk-ручки у бэка нет — каждому
    // блоку свой полный PUT, последовательно (тот же runSequentialPuts, что у
    // реордера): так давление на бэк ограничено, а при отказе на сервере
    // остаётся консистентный префикс, который автор видит в ленте блоков.
    updateBlocksLook: build.mutation<
      void,
      { lessonId: number; updates: Array<{ id: number; body: BlockCreate }> }
    >({
      queryFn: ({ updates }, _api, _extra, baseQuery) =>
        runSequentialPuts(baseQuery, blockPutRequests(updates, withNormalizedBody)),
      async onQueryStarted({ lessonId, updates }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          lessonsApi.util.updateQueryData('getLessonBlocks', lessonId, (draft) => {
            applyLookUpdates(draft, updates)
          }),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      invalidatesTags: (_r, _e, { lessonId }) => [{ type: 'Block', id: `lesson-${lessonId}` }],
    }),
  }),
})

export const {
  useCreateBlockMutation,
  useUpdateBlockMutation,
  useDeleteBlockMutation,
  useReorderBlocksMutation,
  useUpdateBlocksLookMutation,
} = blocksApi
